// Platform Role Types for ToolboxLogic Internal Staff
export type PlatformRole =
  | 'toolboxlogic_community_sp_elect' // Supreme override authority
  | 'toolboxlogic_community_subpanel' // Engineering/operations team
  | 'toolboxlogic_community_support' // Customer success team
  | 'toolboxlogic_community_sales'; // Sales and onboarding

// Community Role Types for Customer Communities
export type CommunityRole =
  | 'community_admin' // Community's highest admin (renamed from super_admin)
  | 'board_director' // Board director (unified board governance role)
  | 'moderator' // Community moderator
  | 'resident' // Community resident
  | 'vendor' // Service provider to community
  | 'bank'; // Bank provider to community

// Banking Role Taxonomy (Bank_Role Cluster)
export type BankRole =
  | 'bank_lender' // Bank lending specialist (existing)
  | 'bank_admin' // Bank administrator
  | 'bank_manager'; // Bank relationship manager

// Legacy Role Types (maintained for backward compatibility)
export type LegacyRole =
  | 'super_admin' // Maps to community_admin
  | 'admin' // Maps to board_director or community role
  | 'user'; // Maps to resident

// All Role Types Combined
export type AllRoles = PlatformRole | CommunityRole | BankRole | LegacyRole;

// Role Authority Levels
export enum RoleAuthority {
  PLATFORM_SUPREME = 'platform_supreme', // SP-Elect
  PLATFORM_TECHNICAL = 'platform_technical', // Subpanel
  PLATFORM_SUPPORT = 'platform_support', // Support/Sales
  COMMUNITY_ADMIN = 'community_admin', // Community admin
  COMMUNITY_GOVERNANCE = 'community_governance', // Board roles
  COMMUNITY_MODERATION = 'community_moderation', // Moderators
  COMMUNITY_MEMBER = 'community_member', // Residents
  SERVICE_PROVIDER = 'service_provider', // Vendors/Banks
  LEGACY = 'legacy', // Legacy compatibility
}

// Role Permissions Interface
export interface RolePermissions {
  // Platform Administration
  platformSettings: boolean;
  crossCommunityAccess: boolean;
  emergencyOverride: boolean;
  auditAccess: boolean;

  // Community Management
  communityAdmin: boolean;
  userManagement: boolean;
  contentModeration: boolean;

  // Governance
  governance: boolean;
  voting: boolean;
  boardActions: boolean;

  // Financial
  financialAccess: boolean;
  bankingOperations: boolean;
  paymentProcessing: boolean;

  // Service Delivery
  serviceManagement: boolean;
  workOrders: boolean;

  // Data Access
  readOnlyAccess: boolean;
  writeAccess: boolean;
  restrictedAccess: boolean;
}

// Role Configuration
export interface RoleConfig {
  role: AllRoles;
  authority: RoleAuthority;
  permissions: RolePermissions;
  communityScoped: boolean; // True if role is limited to single community
  platformRole: boolean; // True if this is a ToolboxLogic platform role
  legacyRole: boolean; // True if this is a legacy role needing migration
  description: string;
}

// Role Mapping for Legacy Compatibility
export const roleMappings: Record<LegacyRole, CommunityRole | BankRole> = {
  super_admin: 'community_admin',
  admin: 'board_director',
  user: 'resident',
};

// Role Authority Mapping
export const roleAuthorityMap: Record<AllRoles, RoleAuthority> = {
  // Platform Roles
  toolboxlogic_community_sp_elect: RoleAuthority.PLATFORM_SUPREME,
  toolboxlogic_community_subpanel: RoleAuthority.PLATFORM_TECHNICAL,
  toolboxlogic_community_support: RoleAuthority.PLATFORM_SUPPORT,
  toolboxlogic_community_sales: RoleAuthority.PLATFORM_SUPPORT,

  // Community Roles
  community_admin: RoleAuthority.COMMUNITY_ADMIN,
  board_director: RoleAuthority.COMMUNITY_GOVERNANCE,
  moderator: RoleAuthority.COMMUNITY_MODERATION,
  resident: RoleAuthority.COMMUNITY_MEMBER,
  vendor: RoleAuthority.SERVICE_PROVIDER,
  bank: RoleAuthority.SERVICE_PROVIDER,

  // Bank Roles
  bank_lender: RoleAuthority.SERVICE_PROVIDER,
  bank_admin: RoleAuthority.SERVICE_PROVIDER,
  bank_manager: RoleAuthority.SERVICE_PROVIDER,

  // Legacy Roles
  super_admin: RoleAuthority.LEGACY,
  admin: RoleAuthority.LEGACY,
  user: RoleAuthority.LEGACY,
};

// Platform Role Permissions
const platformPermissions: Record<PlatformRole, RolePermissions> = {
  toolboxlogic_community_sp_elect: {
    platformSettings: true,
    crossCommunityAccess: true,
    emergencyOverride: true,
    auditAccess: true,
    communityAdmin: true,
    userManagement: true,
    contentModeration: true,
    governance: true,
    voting: true,
    boardActions: true,
    financialAccess: true,
    bankingOperations: true,
    paymentProcessing: true,
    serviceManagement: true,
    workOrders: true,
    readOnlyAccess: true,
    writeAccess: true,
    restrictedAccess: true,
  },
  toolboxlogic_community_subpanel: {
    platformSettings: true,
    crossCommunityAccess: true,
    emergencyOverride: false, // Requires SP-Elect authorization
    auditAccess: true,
    communityAdmin: true,
    userManagement: true,
    contentModeration: true,
    governance: false, // Cannot override community governance
    voting: false, // Cannot access voting ledgers
    boardActions: false,
    financialAccess: false, // Limited financial access
    bankingOperations: false,
    paymentProcessing: false,
    serviceManagement: true,
    workOrders: true,
    readOnlyAccess: true,
    writeAccess: true, // Excludes voting ledgers and governance fraud prevention
    restrictedAccess: false,
  },
  toolboxlogic_community_support: {
    platformSettings: false,
    crossCommunityAccess: false, // Assigned communities only
    emergencyOverride: false,
    auditAccess: false,
    communityAdmin: false,
    userManagement: false,
    contentModeration: false,
    governance: false,
    voting: false,
    boardActions: false,
    financialAccess: false,
    bankingOperations: false,
    paymentProcessing: false,
    serviceManagement: false,
    workOrders: false,
    readOnlyAccess: true,
    writeAccess: false, // Limited to support resolution
    restrictedAccess: false,
  },
  toolboxlogic_community_sales: {
    platformSettings: false,
    crossCommunityAccess: false, // Prospects only
    emergencyOverride: false,
    auditAccess: false,
    communityAdmin: false,
    userManagement: false,
    contentModeration: false,
    governance: false,
    voting: false,
    boardActions: false,
    financialAccess: false,
    bankingOperations: false,
    paymentProcessing: false,
    serviceManagement: false,
    workOrders: false,
    readOnlyAccess: true, // Limited to onboarding data
    writeAccess: false,
    restrictedAccess: false,
  },
};

// Community Role Permissions
const communityPermissions: Record<CommunityRole, RolePermissions> = {
  community_admin: {
    platformSettings: false,
    crossCommunityAccess: false,
    emergencyOverride: false,
    auditAccess: false,
    communityAdmin: true,
    userManagement: true,
    contentModeration: true,
    governance: true,
    voting: true,
    boardActions: true,
    financialAccess: true, // Community financial data only
    bankingOperations: false,
    paymentProcessing: true,
    serviceManagement: true,
    workOrders: true,
    readOnlyAccess: true,
    writeAccess: true,
    restrictedAccess: false,
  },
  board_director: {
    platformSettings: false,
    crossCommunityAccess: false,
    emergencyOverride: false,
    auditAccess: false,
    communityAdmin: false,
    userManagement: false,
    contentModeration: false,
    governance: true,
    voting: true,
    boardActions: true,
    financialAccess: true, // Board directors have financial access
    bankingOperations: false,
    paymentProcessing: true,
    serviceManagement: false,
    workOrders: false,
    readOnlyAccess: true,
    writeAccess: true, // Governance and financial
    restrictedAccess: false,
  },
  moderator: {
    platformSettings: false,
    crossCommunityAccess: false,
    emergencyOverride: false,
    auditAccess: false,
    communityAdmin: false,
    userManagement: true, // Limited user management
    contentModeration: true,
    governance: false,
    voting: true,
    boardActions: false,
    financialAccess: false,
    bankingOperations: false,
    paymentProcessing: false,
    serviceManagement: false,
    workOrders: false,
    readOnlyAccess: true,
    writeAccess: true, // Moderation only
    restrictedAccess: false,
  },
  resident: {
    platformSettings: false,
    crossCommunityAccess: false,
    emergencyOverride: false,
    auditAccess: false,
    communityAdmin: false,
    userManagement: false,
    contentModeration: false,
    governance: false,
    voting: true,
    boardActions: false,
    financialAccess: false,
    bankingOperations: false,
    paymentProcessing: false,
    serviceManagement: false,
    workOrders: false,
    readOnlyAccess: true,
    writeAccess: false,
    restrictedAccess: false,
  },
  vendor: {
    platformSettings: false,
    crossCommunityAccess: false,
    emergencyOverride: false,
    auditAccess: false,
    communityAdmin: false,
    userManagement: false,
    contentModeration: false,
    governance: false,
    voting: false,
    boardActions: false,
    financialAccess: false,
    bankingOperations: false,
    paymentProcessing: false,
    serviceManagement: true,
    workOrders: true,
    readOnlyAccess: true,
    writeAccess: true, // Service-related only
    restrictedAccess: false,
  },
  bank: {
    platformSettings: false,
    crossCommunityAccess: false,
    emergencyOverride: false,
    auditAccess: false,
    communityAdmin: false,
    userManagement: false,
    contentModeration: false,
    governance: false,
    voting: false,
    boardActions: false,
    financialAccess: true, // Banking data only
    bankingOperations: true,
    paymentProcessing: true,
    serviceManagement: false,
    workOrders: false,
    readOnlyAccess: true,
    writeAccess: true, // Financial only
    restrictedAccess: false,
  },
};

// Bank Role Permissions
const bankPermissions: Record<BankRole, RolePermissions> = {
  bank_lender: {
    platformSettings: false,
    crossCommunityAccess: false,
    emergencyOverride: false,
    auditAccess: false,
    communityAdmin: false,
    userManagement: false,
    contentModeration: false,
    governance: false,
    voting: false,
    boardActions: false,
    financialAccess: true,
    bankingOperations: true,
    paymentProcessing: true,
    serviceManagement: false,
    workOrders: false,
    readOnlyAccess: true,
    writeAccess: true, // Lending operations
    restrictedAccess: false,
  },
  bank_admin: {
    platformSettings: false,
    crossCommunityAccess: false,
    emergencyOverride: false,
    auditAccess: false,
    communityAdmin: false,
    userManagement: false,
    contentModeration: false,
    governance: false,
    voting: false,
    boardActions: false,
    financialAccess: true,
    bankingOperations: true,
    paymentProcessing: true,
    serviceManagement: false,
    workOrders: false,
    readOnlyAccess: true,
    writeAccess: true, // Bank administration
    restrictedAccess: false,
  },
  bank_manager: {
    platformSettings: false,
    crossCommunityAccess: false,
    emergencyOverride: false,
    auditAccess: false,
    communityAdmin: false,
    userManagement: false,
    contentModeration: false,
    governance: false,
    voting: false,
    boardActions: false,
    financialAccess: true,
    bankingOperations: true,
    paymentProcessing: true,
    serviceManagement: false,
    workOrders: false,
    readOnlyAccess: true,
    writeAccess: true, // Relationship management
    restrictedAccess: false,
  },
};

// Role Validation and Utilities
export class RoleManager {
  static getAllPermissions(): Record<AllRoles, RolePermissions> {
    return {
      ...platformPermissions,
      ...communityPermissions,
      ...bankPermissions,
      // Legacy roles map to their modern equivalents
      super_admin: communityPermissions.community_admin,
      admin: communityPermissions.board_director,
      user: communityPermissions.resident,
    };
  }

  static getPermissions(role: AllRoles): RolePermissions {
    const allPermissions = this.getAllPermissions();
    return allPermissions[role] || communityPermissions.resident; // Default to resident
  }

  static hasPermission(role: AllRoles, permission: keyof RolePermissions): boolean {
    const permissions = this.getPermissions(role);
    return permissions[permission] || false;
  }

  static isPlatformRole(role: AllRoles): boolean {
    return role.startsWith('toolboxlogic_');
  }

  static isCommunityRole(role: AllRoles): boolean {
    const communityRoles: AllRoles[] = [
      'community_admin',
      'board_director',
      'moderator',
      'resident',
      'vendor',
      'bank',
    ];
    return communityRoles.includes(role);
  }

  static isBankRole(role: AllRoles): boolean {
    const bankRoles: AllRoles[] = ['bank_lender', 'bank_admin', 'bank_manager'];
    return bankRoles.includes(role) || role === 'bank';
  }

  static isLegacyRole(role: AllRoles): boolean {
    const legacyRoles: AllRoles[] = ['super_admin', 'admin', 'user'];
    return legacyRoles.includes(role);
  }

  static migrateRole(legacyRole: LegacyRole): CommunityRole | BankRole {
    return roleMappings[legacyRole];
  }

  static getAuthority(role: AllRoles): RoleAuthority {
    return roleAuthorityMap[role] || RoleAuthority.COMMUNITY_MEMBER;
  }

  static getRoleConfig(role: AllRoles): RoleConfig {
    return {
      role,
      authority: this.getAuthority(role),
      permissions: this.getPermissions(role),
      communityScoped: !this.isPlatformRole(role),
      platformRole: this.isPlatformRole(role),
      legacyRole: this.isLegacyRole(role),
      description: this.getRoleDescription(role),
    };
  }

  static getRoleDescription(role: AllRoles): string {
    const descriptions: Record<AllRoles, string> = {
      // Platform Roles
      toolboxlogic_community_sp_elect: 'Supreme platform authority with override capabilities',
      toolboxlogic_community_subpanel: 'Technical support and implementation team',
      toolboxlogic_community_support: 'Customer success and support specialist',
      toolboxlogic_community_sales: 'Sales and onboarding specialist',

      // Community Roles
      community_admin: 'Highest authority within the community',
      board_director: 'Community governance director (Sub-Elect authority)',
      moderator: 'Community moderator',
      resident: 'Community resident',
      vendor: 'Service provider to the community',
      bank: 'Banking service provider',

      // Bank Roles
      bank_lender: 'Bank lending specialist',
      bank_admin: 'Bank administrator',
      bank_manager: 'Bank relationship manager',

      // Legacy Roles
      super_admin: 'Legacy super admin (maps to community_admin)',
      admin: 'Legacy admin (maps to board_director)',
      user: 'Legacy user (maps to resident)',
    };

    return descriptions[role] || 'Unknown role';
  }
}

// Export all role configurations
export const ALL_ROLE_CONFIGS: Record<AllRoles, RoleConfig> = Object.fromEntries(
  Object.keys(roleAuthorityMap).map((role) => [role, RoleManager.getRoleConfig(role as AllRoles)]),
) as Record<AllRoles, RoleConfig>;
